"use client";

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { addressService } from "@/services/address";
import { Modal } from "@/components/modal/modal";
import FormInput from "@/app/checkout/components/form-input/form-input";
import { TextArea } from "@/components/text-area/text-area";
import { ConfirmModal } from "@/components/confirm-modal/confirm-modal";
import DeleteBinLineIcon from "remixicon-react/DeleteBinLineIcon";
import AddLineIcon from "remixicon-react/AddLineIcon";
import useUserStore from "@/global-store/user";
import { useFormik, FormikProvider } from "formik";
import { countryList } from "@/utils/countryData";
import { OptionType } from "@/types/checkout";
import SelectInput from "@/app/checkout/components/select-input";
import { UserAddress } from "@/types/address";

interface SavedAddressesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SavedAddressesModal = ({
  isOpen,
  onClose,
}: SavedAddressesModalProps) => {
  const user = useUserStore((state) => state.user);
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addressToDelete, setAddressToDelete] = useState<number | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<OptionType | null>(
    null,
  );

  const { data, isLoading } = useQuery({
    queryKey: ["saved-addresses"],
    queryFn: addressService.getUserAddresses,
    enabled: isOpen,
  });

  const addresses = data?.data?.addresses ?? [];

  const addMutation = useMutation({
    mutationFn: addressService.addUserAddress,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-addresses"] });
    },
    onError: (err: unknown) => {
      const e = err as {
        response?: { data?: { message?: string | string[] } };
        message?: string;
      };
      const msg =
        e?.response?.data?.message || e?.message || "Failed to add address";
      setError(Array.isArray(msg) ? msg.join(", ") : msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: addressService.deleteUserAddress,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-addresses"] });
    },
  });

  const formik = useFormik({
    initialValues: {
      address: "",
      country: "",
      city: "",
      zipCode: "",
      notes: "",
    },
    onSubmit: (values) => {
      if (!user?.id) {
        setError("User not found");
        return;
      }
      addMutation.mutate(
        {
          userId: user.id,
          address: values.address,
          city: values.city,
          country: values.country,
          zipCode: values.zipCode,
          notes: values.notes,
        },
        {
          onSuccess: () => {
            setShowForm(false);
            setError(null);
            formik.resetForm();
            setSelectedCountry(null);
          },
        },
      );
    },
  });

  const handleCountryChange = (name: string, option: OptionType | null) => {
    if (option) {
      const { value, label } = option;
      setSelectedCountry({ label, value });
      formik.setFieldValue("country", value);
    } else {
      setSelectedCountry(null);
      formik.setFieldValue("country", "");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      withCloseButton
      size="large"
      fixedButton
    >
      <div className="p-6">
        <h2 className="text-xl font-bold mb-6">Saved Addresses</h2>

        <div className="space-y-4">
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed border-[#1B9B9B]/40 rounded-xl text-[#1B9B9B] hover:bg-[#1B9B9B]/5 font-medium"
            >
              <AddLineIcon size={18} />
              Add New Address
            </button>
          ) : (
            <div className="border border-[#1B9B9B] rounded-xl p-5 bg-white">
              <h3 className="font-semibold mb-4">New Address</h3>

              <FormikProvider value={formik}>
                <form onSubmit={formik.handleSubmit} className="space-y-4">
                  <FormInput
                    title="Address"
                    name="address"
                    sx="flex flex-col w-full"
                  />

                  <div className="flex flex-col md:flex-row gap-4 w-full">
                    <div className="my-2 w-full md:w-1/3">
                      <SelectInput
                        name="country"
                        title="Country"
                        sx="flex flex-col w-full mt-[-7px]"
                        placeholder="Select..."
                        onChange={handleCountryChange}
                        options={countryList}
                        value={selectedCountry}
                      />
                    </div>

                    <FormInput
                      title="City"
                      name="city"
                      sx="flex flex-col w-full md:w-1/3"
                    />
                    <FormInput
                      title="Zip Code"
                      name="zipCode"
                      sx="flex flex-col w-full md:w-1/3"
                    />
                  </div>

                  <div className="w-full">
                    <label className="text-sm text-[#00000080] mb-2 block">
                      Notes (Optional)
                    </label>
                    <TextArea
                      name="notes"
                      placeholder="Notes about this address"
                      value={formik.values.notes}
                      onChange={formik.handleChange}
                      rows={3}
                    />
                  </div>

                  {error && <p className="text-red text-sm">{error}</p>}

                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={addMutation.isLoading}
                      className="bg-[#1B9B9B] text-white rounded-lg px-6 py-2.5 font-medium disabled:opacity-70 transition-colors"
                    >
                      {addMutation.isLoading ? "Saving..." : "Save address"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setError(null);
                        formik.resetForm();
                        setSelectedCountry(null);
                      }}
                      className="px-6 py-2.5 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </FormikProvider>
            </div>
          )}

          {isLoading ? (
            <div className="h-24 animate-pulse bg-gray-100 rounded-xl" />
          ) : (
            <div className="flex flex-nowrap overflow-x-auto gap-4 pb-2">
              {addresses.map((address: UserAddress) => (
                <div
                  key={address.id}
                  className="flex-shrink-0 min-w-[280px] p-5 rounded-xl border border-[#1B9B9B] bg-white"
                >
                  <div className="flex justify-between mb-2">
                    <h4 className="font-bold">{address.notes}</h4>
                    <button
                      onClick={() => setAddressToDelete(address.id)}
                      disabled={deleteMutation.isLoading}
                      className="text-gray-300 hover:text-red-500"
                    >
                      <DeleteBinLineIcon size={18} />
                    </button>
                  </div>

                  <p className="text-sm text-gray-600">{address.address}</p>
                  <p className="text-sm text-gray-600">
                    {address.city}, {address.country} - {address.zipCode}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={addressToDelete !== null}
        title="Delete Address"
        text="Are you sure you want to delete this address? This action cannot be undone."
        onConfirm={() => {
          if (addressToDelete !== null) {
            deleteMutation.mutate(addressToDelete);
            setAddressToDelete(null);
          }
        }}
        onCancel={() => setAddressToDelete(null)}
        loading={deleteMutation.isLoading}
        confirmText="Delete"
      />
    </Modal>
  );
};
